
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { Database } from '@/integrations/supabase/types';

// Use the WebsiteSection type directly from the Database types
type SectionType = Database['public']['Enums']['website_section'];

interface ImageFetcherOptions {
  section: SectionType;
  subsection?: string;
  sortDirection?: 'asc' | 'desc';
  limit?: number;
}

export const useImageFetcher = ({ 
  section, 
  subsection,
  sortDirection = 'desc',
  limit 
}: ImageFetcherOptions) => {
  const { data: images, refetch } = useQuery({
    queryKey: ["images", section, subsection, sortDirection, limit],
    queryFn: async () => {
      console.log(`Fetching images for ${section}${subsection ? ' / ' + subsection : ''}`);
      
      let query = supabase
        .from("images")
        .select("*")
        .eq("section", section)
        .order('created_at', { ascending: sortDirection === 'asc' });
      
      if (subsection) {
        query = query.eq("subsection", subsection);
      }
      
      if (limit) {
        query = query.limit(limit);
      }
      
      const { data, error } = await query;
      
      if (error) {
        console.error("Error fetching images:", error);
        throw error;
      }
      
      console.log("Fetched images:", data);
      return data;
    },
  });

  const getBackgroundImage = (fallbackUrl: string) => {
    if (images && images.length > 0) {
      const { data } = supabase.storage
        .from('images')
        .getPublicUrl(images[0].storage_path);
      return data.publicUrl;
    }
    return fallbackUrl;
  };

  return { images, getBackgroundImage, refetch };
};
